import { useIntl } from 'react-intl';

import {
  CartActionType,
  CartProduct,
  useCartDispatch,
} from 'public/context/CartContext';
import AmountSelector from 'ui/CartAmountSelector/CartAmountSelector';
import { Icon } from 'ui/Icon';
import { FallbackImage } from 'ui/UploadImage/fallBack/FallBackImage';
import { ProductUnityPrice } from 'utils/productCardHelpers';
import { CartProductPrice } from 'utils/productTotalPriceHelper';

import { StyledCartCard } from './style';
import { useNavigate } from 'react-router-dom';
import { options } from 'config/config';
import useMarketType from '../../utils/useMarketType';
import { publicPath } from 'enums/publicRoutes';

const DEFAULT_IMAGE = options.images.defaultProductImage;

export interface CartCardProps {
  product: CartProduct;
  isView?: boolean | undefined;
}

export const CartCard = ({ product, isView }: CartCardProps) => {
  const intl = useIntl();
  const cartDispatch = useCartDispatch();
  const navigate = useNavigate();
  const { isEmarket } = useMarketType();

  const cartProductAmount = product?.cartProduct?.product_amounts?.find(
    (amount) => amount.type === product.cartType
  );
  const reservation = cartProductAmount?.wholesale_reserve;
  const uniqueMarks = product.cartProduct?.qualitymarks?.filter(
    (q, i, arr) =>
      arr.findIndex((x) => x.quality_mark_id === q.quality_mark_id) === i
  );

  return (
    <StyledCartCard className="cart-product">
      <div className={'cart-products-content'}>
        <div className={'cart-cell-1'}>
          {!isView && (
            <Icon
              onClick={() =>
                product?.cartProduct &&
                cartDispatch({
                  type: CartActionType.REMOVE_ITEM,
                  productId: product.cartProduct.id,
                  farmId: product.cartProduct.farm_id,
                })
              }
              name={'cart-product-colse-button'}
              className={'cart-products-remove-button'}
            />
          )}
          <FallbackImage
            src={
              product.cartProduct?.image_small
                ? product.cartProduct?.image_small
                : product.cartProduct?.farm?.logo
                  ? product.cartProduct?.farm?.logo
                  : DEFAULT_IMAGE
            }
            type={'ProductCard'}
          />
        </div>
        <div className={'cart-cell-2'}>
          <p
            className={'cart-product-farm-name'}
            onClick={() =>
              navigate(
                isEmarket
                  ? publicPath.farm(
                      product.cartProduct?.farm?.slug,
                      product.cartProduct?.farm_id
                    )
                  : publicPath.wholesaleFarm(
                      product.cartProduct?.farm?.slug,
                      product.cartProduct?.farm_id
                    )
              )
            }
          >
            {product.cartProduct?.farm_name}
          </p>
          <div className="cart-product-name-row">
            <p
              className={'cart-product-name'}
              onClick={() =>
                navigate(
                  isEmarket
                    ? publicPath.product(
                        product.cartProduct?.slug,
                        product.cartProduct?.id
                      )
                    : publicPath.wholesaleProduct(
                        product?.cartProduct?.slug,
                        product?.cartProduct?.id
                      )
                )
              }
            >
              {product.cartProduct?.name}
            </p>
            {!isEmarket && !!uniqueMarks?.length && (
              <div className="cart-product-quality-marks">
                {uniqueMarks.map((qmark, index) => (
                  <FallbackImage
                    className="quality-mark"
                    src={qmark.quality_mark?.logo ?? ''}
                    type={'notFound'}
                    preview={false}
                    key={index}
                  />
                ))}
              </div>
            )}
          </div>
          {!isEmarket && (
            <div className="cart-product-stock-info">
              <div className="cart-product-availability">
                {intl.formatMessage({ id: 'general.availability_amount' })}
                {': '}
                {cartProductAmount?.storage_stock_count ?? 0}{' '}
                {cartProductAmount?.storage_unit}
              </div>
              {!!reservation?.amount_to && (
                <div className="cart-product-reservation">
                  {intl.formatMessage({ id: 'general.for_reservation' })}
                  {': '}
                  {reservation.amount_from}-{reservation.amount_to}{' '}
                  {cartProductAmount?.storage_unit}
                </div>
              )}
            </div>
          )}
        </div>
        <div className={'cart-cell-3'}>
          <div className={'price-container'}>
            <ProductUnityPrice productAmount={cartProductAmount} />
            <CartProductPrice product={product} type={'PER_UNIT'} />
          </div>
        </div>
        <div className={'cart-cell-4'}>
          {isView ? (
            <div className={'cart-product-amount-view'}>
              {'x' + product.amount}
            </div>
          ) : (
            <AmountSelector
              amountCart={product.amount}
              product={product.cartProduct}
              unitType={cartProductAmount?.storage_unit}
            />
          )}
        </div>
        <div className={'cart-cell-5'}>
          <div className={'price-container'}>
            <CartProductPrice product={product} type={'TOTAL'} />
          </div>
        </div>
      </div>

      <div className="mobile-cart-cells">
        <div className={'cart-cell-4'}>
          <AmountSelector
            amountCart={product.amount}
            product={product.cartProduct}
            unitType={cartProductAmount?.storage_unit}
          />
        </div>

        <div className={'cart-cell-5'}>
          <span className="label">
            {intl.formatMessage({ id: 'orders.summary' }) + ': '}
          </span>
          <div className={'price-container'}>
            <CartProductPrice product={product} type={'TOTAL'} />
          </div>
        </div>
      </div>
      {!isEmarket && (
        <div className="mobile-stock-info">
          <div className="cart-product-availability">
            {intl.formatMessage({ id: 'general.availability_amount' })}
            {': '}
            {cartProductAmount?.storage_stock_count ?? 0}{' '}
            {cartProductAmount?.storage_unit}
          </div>
          {!!reservation?.amount_to && (
            <div className="cart-product-reservation">
              {intl.formatMessage({ id: 'general.for_reservation' })}
              {': '}
              {reservation.amount_from}-{reservation.amount_to}{' '}
              {cartProductAmount?.storage_unit}
            </div>
          )}
        </div>
      )}
    </StyledCartCard>
  );
};
